import { memo } from 'react';
import { OrderCard } from './OrderCard';
import type { OrderWithItems } from '@/store/seller';
import type { OrderStatus } from '@/types';

interface OrderListProps {
  orders: OrderWithItems[];
  activeTab: OrderStatus;
  onActionClick: (orderId: string, action: string) => void | Promise<void>;
  onDetailClick?: (order: OrderWithItems) => void;
  showFilter?: boolean;
  filterChecked?: boolean;
  onFilterChange?: (checked: boolean) => void;
  loading?: boolean;
}

export const OrderList = memo(function OrderList({ 
  orders, 
  activeTab, 
  onActionClick,
  onDetailClick,
  showFilter = false,
  filterChecked = false,
  onFilterChange,
  loading = false 
}: OrderListProps) {
  const getVariant = (status: OrderStatus): 'pending' | 'processing' | 'completed' | 'cancelled' => {
    switch (status) {
      case 'PENDING':
        return 'pending';
      case 'ACCEPTED':
        return 'processing';
      case 'READY':
      case 'COMPLETED':
        return 'completed';
      case 'CANCELLED':
      case 'NOSHOW':
        return 'cancelled';
      default:
        return 'pending';
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-20 mb-3"></div>
            <div className="space-y-2">
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
            <div className="h-8 bg-gray-200 rounded mt-4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (orders.length === 0) {
    const emptyMessages: Record<OrderStatus, string> = {
      PENDING: '접수 대기 중인 주문이 없습니다.',
      ACCEPTED: '처리 중인 주문이 없습니다.',
      READY: showFilter && !filterChecked ? '정상 완료된 주문이 없습니다.' : '완료/종료된 주문이 없습니다.',
      COMPLETED: '완료된 주문이 없습니다.',
      CANCELLED: '취소된 주문이 없습니다.',
      NOSHOW: '노쇼 주문이 없습니다.'
    };

    return (
      <div>
        {showFilter && (
          <div className="flex justify-end mb-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={filterChecked}
                onChange={(e) => onFilterChange?.(e.target.checked)}
                className="w-4 h-4 text-green-600 rounded focus:ring-green-500 focus:ring-2"
              />
              <span className="text-gray-700">✓ 취소/환불/노쇼 주문 보기</span>
            </label>
          </div>
        )}
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">📋</div>
          <p className="text-gray-500">{emptyMessages[activeTab]}</p>
        </div>
      </div>
    );
  }

  const variant = getVariant(activeTab);

  return (
    <div>
      {showFilter && (
        <div className="flex justify-end mb-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={filterChecked}
              onChange={(e) => onFilterChange?.(e.target.checked)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500 focus:ring-2"
            />
            <span className="text-gray-700">✓ 취소/환불/노쇼 주문 보기</span>
          </label>
        </div>
      )}
      <div className="space-y-3">
        {orders.map((order) => (
          <OrderCard
            key={order.id}
            order={order}
            onActionClick={onActionClick}
            onDetailClick={onDetailClick}
            variant={variant}
          />
        ))}
      </div>
    </div>
  );
});